import Box from "@mui/material/Box";
import Masonry from "@mui/lab/Masonry";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useCallback, useEffect, useRef, useState } from "react";
import { MediaModal } from "./MediaModal";
import MemoryCard from "./MemoryCard";
import { fetchMemories } from "../lib/queries/fetchMemories";
import toast from "react-hot-toast";

export interface MemoryItem {
  id: string;
  type: "image" | "video" | "text" | "group";
  media?: string;
  mediaList?: string[];
  text?: string;
  from?: string;
}

function LoadingSkeleton() {
  return (
    <Stack spacing={2} sx={{ padding: 2 }}>
      <Skeleton variant="rectangular" width="100%" height={200} />
      <Skeleton variant="text" />
      <Skeleton variant="text" width="60%" />
    </Stack>
  );
}

export default function MemoryWall() {
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [open, setOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const initialFetchRef = useRef(false);
  const firstObserverSkip = useRef(true);

  // --- Load More ---
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const res = await fetchMemories(lastDoc);
      setMemories((prev) => [...prev, ...res.data]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load memories";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [lastDoc, loading, hasMore]);

  // --- Initial Load (only once) ---
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;

    loadMore().then(() => setInitialLoaded(true));
  }, [loadMore]);

  // --- Infinite Scroll Observer ---
  useEffect(() => {
    if (!initialLoaded) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (firstObserverSkip.current) {
            firstObserverSkip.current = false;
            return;
          }
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoaded]);

  // --- Modal Handlers ---
  const handleOpen = (media: string) => {
    setSelectedMedia(media);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMedia(null);
  };

  return (
    <Box>
      <Masonry columns={{ xs: 1, sm: 2, md: 3 }} spacing={3}>
        {memories.map((item) => (
          <MemoryCard key={item.id} item={item} onOpen={handleOpen} />
        ))}
        {loading &&
          [...Array(3)].map((_, i) => <LoadingSkeleton key={`sk-${i}`} />)}
      </Masonry>

      {hasMore && <div ref={loaderRef} style={{ height: 50 }} />}

      <MediaModal open={open} media={selectedMedia} onClose={handleClose} />
    </Box>
  );
}
